// src/Components/Promo/MailingList.js

import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Typography,
  TextField,
  Autocomplete,
  InputAdornment,
  TableSortLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useMailingListManage } from '../../Hooks/useMailingListManage';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import {
  MenuAddNewGrey,
  MenuSaveGrey,
  MenuDelete,
  MenuIconCancel,
  MenuDeleteGrey,
  MenuDownload,
} from '../../assets/icons/CustomIcons';

// --- CONSTANTS ---
const DARK_BLUE = '#00008B';

// --- CUSTOM TRIANGLE ICON ---
const CustomSortIcon = ({ active, className }) => {
  return (
    <svg className={className} focusable='false' viewBox='0 0 24 24' aria-hidden='true'>
      {active ? (
        <path d='M12 8l6 8H6z' fill='currentColor' />
      ) : (
        <path
          d='M12 8l6 8H6z'
          fill='none'
          stroke='currentColor'
          strokeWidth='1'
          strokeLinejoin='round'
        />
      )}
    </svg>
  );
};

export default function MailingList({ setIsDirty }) {
  const { state, flags, alerts, actions } = useMailingListManage();

  // --- SEARCH STATES ---
  const [searchVal, setSearchVal] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [activeSearchOptions, setActiveSearchOptions] = useState([]);

  // Base options generated from rows
  const baseSearchOptions = React.useMemo(() => {
    if (!state.displayRows) return [];
    return state.displayRows.map((row) => ({
      label: `${row.userName || 'No Name'} | ${row.companyName || 'No Company'} | ${row.email || 'No Email'}`,
      id: row.id,
    }));
  }, [state.displayRows]);

  useEffect(() => {
    setSearchVal(null);
    setSearchInputValue('');
    setActiveSearchOptions([]);
    if (actions.setFocusedRowId) {
      actions.setFocusedRowId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (setIsDirty) setIsDirty(flags.isDirty);
  }, [flags.isDirty, setIsDirty]);

  const ICON_SIZE = 64;
  const toolBlockStyle = {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minWidth: 0,
  };

  const formatDate = (dateVal) => {
    if (!dateVal) return '';
    if (typeof dateVal.toDate === 'function') {
      return dateVal.toDate().toLocaleDateString();
    }
    return new Date(dateVal).toLocaleDateString();
  };

  const handleDownload = () => {
    if (!state.displayRows || state.displayRows.length === 0) return;

    const now = new Date().toLocaleString();
    const headerRow = ['Date', 'User Name', 'Company', 'Email', now];
    const dataRows = state.displayRows.map((row) => [
      formatDate(row.addedAt),
      row.userName,
      row.companyName || '',
      row.email,
      '',
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);

    const wscols = [{ wch: 15 }, { wch: 25 }, { wch: 30 }, { wch: 40 }, { wch: 25 }];
    worksheet['!cols'] = wscols;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Mailing List');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });

    saveAs(data, `MailingList_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const disableDownload = !state.displayRows || state.displayRows.length === 0;

  // --- SEARCH HANDLERS ---
  const handleSearchSelect = (event, newValue) => {
    setSearchVal(newValue);
    if (newValue && newValue.id) {
      if (actions.setFocusedRowId) {
        actions.setFocusedRowId(newValue.id);
      }
      setTimeout(() => {
        const el = document.getElementById(`row-${newValue.id}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
    } else {
      setSearchVal(null);
      setSearchInputValue('');
      setActiveSearchOptions([]);
      if (actions.setFocusedRowId) {
        actions.setFocusedRowId(null);
      }
    }
  };

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === 'clear') {
      setSearchVal(null);
      setSearchInputValue('');
      setActiveSearchOptions([]);
      if (actions.setFocusedRowId) actions.setFocusedRowId(null);
    } else {
      setSearchInputValue(newInputValue);
      if (newInputValue && newInputValue.trim().length > 0) {
        setActiveSearchOptions(baseSearchOptions);
      } else {
        setActiveSearchOptions([]);
        if (actions.setFocusedRowId) actions.setFocusedRowId(null);
      }
    }
  };

  // --- REUSABLE COLUMN HEADER STYLING ---
  const headerSx = {
    backgroundColor: '#fff3e0',
    fontWeight: 'bold',
    borderBottom: '2px solid #ff9800',
    py: '4px',
    textAlign: 'center',
    color: 'black',
  };

  const sortLabelSx = {
    '&.MuiTableSortLabel-root': { color: 'inherit' },
    '&.MuiTableSortLabel-root:hover': {
      color: 'inherit',
      '& .MuiTableSortLabel-icon': { opacity: 0.6 },
    },
    '& .MuiTableSortLabel-icon': {
      opacity: 0.4,
      width: '1.5em',
      height: '1.5em',
      color: '#000000',
      transition: 'opacity 0.2s, color 0.2s',
    },
    '&.Mui-active': { color: 'inherit' },
    '&.Mui-active .MuiTableSortLabel-icon': { opacity: 1, color: '#000000' },
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* TOOLBAR */}
      <Box
        sx={{
          height: '60px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f4f4f4',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          zIndex: 50,
          px: '5px',
        }}>
        <Box sx={toolBlockStyle}>
          <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled={true}>
            <MenuAddNewGrey sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={handleDownload}
            disabled={disableDownload}>
            <MenuDownload
              sx={{
                fontSize: ICON_SIZE,
                opacity: disableDownload ? 0.35 : 1,
                filter: disableDownload ? 'grayscale(100%)' : 'none',
              }}
              iconScale={5}
              iconX={32}
              iconY={38}
              textY={108}
            />
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled={true}>
            <MenuSaveGrey sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={actions.handleDeleteRequest}
            disabled={!flags.canDelete}>
            {flags.canDelete ? (
              <MenuDelete sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuDeleteGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={() => actions.resetInterface()}>
            <MenuIconCancel sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>
      </Box>

      {/* FILTER & SEARCH BAR */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#fdfdfd',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          py: 1,
          px: 2,
          width: '100%',
          boxSizing: 'border-box',
        }}>
        {/* DATE FILTERS (Left) - Fixed width to balance the layout */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0 }}>
          <TextField
            label='From'
            type='date'
            size='small'
            value={state.dateFrom || ''}
            onChange={(e) => actions.setDateFrom && actions.setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: '150px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#fff',
              },
            }}
          />
          <TextField
            label='To'
            type='date'
            size='small'
            value={state.dateTo || ''}
            onChange={(e) => actions.setDateTo && actions.setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: '150px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#fff',
              },
            }}
          />
        </Box>

        {/* SEARCH BOX (Center/Right) - FlexGrows to take up all remaining available space */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexGrow: 1,
            px: 2,
          }}>
          <Autocomplete
            sx={{ width: '100%', maxWidth: '600px' }}
            value={searchVal}
            inputValue={searchInputValue}
            onChange={handleSearchSelect}
            onInputChange={handleInputChange}
            options={activeSearchOptions}
            getOptionLabel={(option) => option.label}
            clearOnBlur={false}
            componentsProps={{
              popper: {
                style: { zIndex: 100005 },
              },
            }}
            renderOption={(props, option) => (
              <li {...props}>
                <Typography sx={{ color: '#1a0dab', fontSize: '0.85rem' }}>
                  {option.label}
                </Typography>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder='Search by User Name, Company, or Email...'
                variant='outlined'
                size='small'
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'new-password',
                  form: {
                    autoComplete: 'off',
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position='start'>
                        <SearchIcon sx={{ color: DARK_BLUE, ml: 1 }} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#fff',
                  },
                }}
              />
            )}
          />
        </Box>
      </Box>

      {/* CONTENT */}
      <Box
        sx={{
          flexGrow: 1,
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
        {state.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              height: '100%',
              width: '100%',
              boxSizing: 'border-box',
              borderTop: '2px solid #ff9800',
              borderBottom: 'none',
              borderRadius: 0,
              overflowY: 'auto',
            }}>
            <Table stickyHeader size='small' sx={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={{ height: '36px' }}>
                  <TableCell
                    sx={{ width: '40px', textAlign: 'center', p: 0, ...headerSx }}>
                    <Checkbox
                      color='primary'
                      indeterminate={
                        state.selectedIds.length > 0 &&
                        state.selectedIds.length < state.displayRows.length
                      }
                      checked={
                        state.displayRows.length > 0 &&
                        state.selectedIds.length === state.displayRows.length
                      }
                      onChange={(e) => actions.handleSelectAll(e.target.checked)}
                      size='small'
                      sx={{ p: 0.5 }}
                    />
                  </TableCell>

                  {/* DATE COLUMN */}
                  <TableCell
                    sx={{
                      width: '15%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    <TableSortLabel
                      active={state.sortConfig?.key === 'addedAt'}
                      direction={
                        state.sortConfig?.key === 'addedAt'
                          ? state.sortConfig.direction
                          : 'asc'
                      }
                      onClick={() =>
                        actions.requestSort && actions.requestSort('addedAt')
                      }
                      IconComponent={(props) => (
                        <CustomSortIcon
                          {...props}
                          active={state.sortConfig?.key === 'addedAt'}
                        />
                      )}
                      sx={sortLabelSx}>
                      Date
                    </TableSortLabel>
                  </TableCell>

                  {/* USER NAME COLUMN */}
                  <TableCell
                    sx={{
                      width: '20%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    <TableSortLabel
                      active={state.sortConfig?.key === 'userName'}
                      direction={
                        state.sortConfig?.key === 'userName'
                          ? state.sortConfig.direction
                          : 'asc'
                      }
                      onClick={() =>
                        actions.requestSort && actions.requestSort('userName')
                      }
                      IconComponent={(props) => (
                        <CustomSortIcon
                          {...props}
                          active={state.sortConfig?.key === 'userName'}
                        />
                      )}
                      sx={sortLabelSx}>
                      User Name
                    </TableSortLabel>
                  </TableCell>

                  {/* COMPANY COLUMN */}
                  <TableCell
                    sx={{
                      width: '20%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    <TableSortLabel
                      active={state.sortConfig?.key === 'companyName'}
                      direction={
                        state.sortConfig?.key === 'companyName'
                          ? state.sortConfig.direction
                          : 'asc'
                      }
                      onClick={() =>
                        actions.requestSort && actions.requestSort('companyName')
                      }
                      IconComponent={(props) => (
                        <CustomSortIcon
                          {...props}
                          active={state.sortConfig?.key === 'companyName'}
                        />
                      )}
                      sx={sortLabelSx}>
                      Company
                    </TableSortLabel>
                  </TableCell>

                  {/* EMAIL COLUMN */}
                  <TableCell
                    sx={{
                      width: '25%',
                      resize: 'horizontal',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      ...headerSx,
                    }}>
                    <TableSortLabel
                      active={state.sortConfig?.key === 'email'}
                      direction={
                        state.sortConfig?.key === 'email'
                          ? state.sortConfig.direction
                          : 'asc'
                      }
                      onClick={() => actions.requestSort && actions.requestSort('email')}
                      IconComponent={(props) => (
                        <CustomSortIcon
                          {...props}
                          active={state.sortConfig?.key === 'email'}
                        />
                      )}
                      sx={sortLabelSx}>
                      Email
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.displayRows.map((row) => {
                  const isSelected = state.selectedIds.includes(row.id);
                  const isFocusedRow = state.focusedRowId === row.id;

                  return (
                    <TableRow
                      id={`row-${row.id}`}
                      key={row.id}
                      hover
                      selected={isSelected}
                      onClick={() => actions.handleCheckboxChange(row.id)}
                      sx={{
                        height: '32px',
                        cursor: 'pointer',
                        backgroundColor: isFocusedRow
                          ? 'rgba(0, 176, 255, 0.15) !important'
                          : 'inherit',
                        transition: 'background-color 0.5s ease',
                        '& td': { py: '4px' },
                        '&.Mui-selected': { backgroundColor: 'transparent !important' },
                        '&.Mui-selected:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                        },
                      }}>
                      <TableCell sx={{ textAlign: 'center', p: 0 }}>
                        <Checkbox
                          checked={isSelected}
                          size='small'
                          sx={{ pointerEvents: 'none', p: 0.5 }}
                        />
                      </TableCell>

                      <TableCell sx={{ maxWidth: 0, textAlign: 'center' }}>
                        <Typography
                          variant='body2'
                          title={formatDate(row.addedAt)}
                          sx={{
                            color: 'black',
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            textAlign: 'center',
                          }}>
                          {formatDate(row.addedAt)}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ px: 1, maxWidth: 0 }}>
                        <Typography
                          variant='body2'
                          title={row.userName}
                          sx={{
                            color: 'black',
                            minHeight: '20px',
                            px: 1,
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                          {row.userName}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ px: 1, maxWidth: 0 }}>
                        <Typography
                          variant='body2'
                          title={row.companyName}
                          sx={{
                            color: 'black',
                            minHeight: '20px',
                            px: 1,
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                          {row.companyName}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ px: 1, maxWidth: 0 }}>
                        <Typography
                          variant='body2'
                          title={row.email}
                          sx={{
                            color: 'black',
                            minHeight: '20px',
                            px: 1,
                            fontSize: '0.8rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                          {row.email}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* --- DIALOGS --- */}
      <ConfirmDialog
        open={alerts.showUnsaved}
        onClose={() => alerts.setShowUnsaved(false)}
        onConfirm={() => {
          alerts.setShowUnsaved(false);
          actions.resetInterface();
        }}
        message='Cancel without Save will discard your changes?'
        severity='warning'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alerts.showDelete}
        onClose={() => alerts.setShowDelete(false)}
        onConfirm={actions.handleConfirmDelete}
        message='Permanently delete selected users from mailing list?'
        severity='error'
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />

      <ConfirmDialog
        open={alerts.showResultAlert}
        onClose={() => alerts.setShowResultAlert(false)}
        message={alerts.resultMessage}
        severity={alerts.resultSeverity}
        autoClose={alerts.resultSeverity === 'success' ? 3000 : 0}
        onConfirm={() => alerts.setShowResultAlert(false)}
        cancelText={alerts.resultSeverity === 'success' ? undefined : null}
        disableRestoreFocus={true}
        sx={{ zIndex: 100005 }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.1)' } } }}
      />
    </Box>
  );
}
